import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchSelectOption {
  value: string;
  label: string;
  hint?: string;
  keywords?: string;
}

/**
 * Selector con búsqueda por escritura. Evita el <select> nativo cuando hay
 * muchos clientes, proveedores o productos y permite teclado (↑ ↓ Enter Esc).
 */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Selecciona…',
  ariaLabel,
  disabled,
  className,
  emptyLabel = 'Sin resultados',
  id,
}: {
  value: string;
  options: SearchSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
  emptyLabel?: string;
  id?: string;
}) {
  const selected = options.find((option) => option.value === value) ?? null;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useRef(`search-select-${Math.random().toString(36).slice(2, 8)}`);

  useEffect(() => {
    const onDocumentMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) =>
      `${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`
        .toLowerCase()
        .includes(term),
    );
  }, [options, query]);

  useEffect(() => {
    setActive(0);
  }, [query, open]);

  const commit = (option: SearchSelectOption) => {
    onChange(option.value);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  return (
    <div className={`search-select${className ? ` ${className}` : ''}`} ref={rootRef}>
      <input
        ref={inputRef}
        id={id}
        className="input search-select__input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId.current}
        aria-autocomplete="list"
        aria-label={ariaLabel}
        disabled={disabled}
        value={open ? query : selected?.label ?? ''}
        placeholder={selected ? selected.label : placeholder}
        onFocus={() => {
          setOpen(true);
          setQuery('');
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onKeyDown={(event) => {
          if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
            setOpen(true);
            return;
          }
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((current) => Math.min(current + 1, filtered.length - 1));
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((current) => Math.max(current - 1, 0));
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            const option = filtered[active];
            if (option) commit(option);
          }
          if (event.key === 'Escape') {
            setOpen(false);
            setQuery('');
            inputRef.current?.blur();
          }
        }}
      />

      {open ? (
        <ul className="search-select__list" role="listbox" id={listId.current}>
          {filtered.length === 0 ? (
            <li className="search-select__empty">{emptyLabel}</li>
          ) : (
            filtered.map((option, index) => (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  className={`search-select__option${index === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => commit(option)}
                >
                  <strong>{option.label}</strong>
                  {option.hint ? <span>{option.hint}</span> : null}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}
